import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import toast from "react-hot-toast";

import { setToken } from "../utils/tokenStorage";
import Logo from "../components/Logo";
import { Mail, KeyRound, ArrowRight, ArrowLeft, Bug } from "lucide-react";

const PARENT_URL = "https://getkhojo.com";

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: "var(--au-text-2)",
  marginBottom: 8,
};

export default function LoginDebug() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const sendCode = async () => {
    if (!email.trim()) { toast.error("Enter email"); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/login", { email: email.trim() });
      toast.success("Access code dispatched");
      setStep(2);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Failed");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code.trim()) { toast.error("Enter code"); return; }
    setBusy(true);
    try {
      const res = await api.post("/api/auth/verify", {
        email: email.trim(),
        code: code.trim(),
        purpose: "login"
      });
      setToken("aegis_token", res.data.access_token);
      toast.success("Authenticated. Welcome.");
      setTimeout(() => nav("/dashboard"), 500);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Invalid code");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="au-root"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: 20,
        position: "relative",
      }}
    >
      {/* Back to parent */}
      <a
        className="au-pill au-pill-glass au-pill-sm"
        href={PARENT_URL}
        style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}
      >
        <ArrowLeft size={16} /> Back
      </a>

      <div
        className="au-glass au-pad-lg"
        style={{ maxWidth: 440, width: "100%", position: "relative", zIndex: 5 }}
      >
        {/* Brand header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <Logo size="large" />
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: "var(--au-muted)",
              marginTop: 10,
            }}
          >
            Find · Fix · Ship
          </div>
        </div>

        {/* Form */}
        {step === 1 ? (
          <>
            <div className="au-marker" style={{ marginBottom: 10 }}>/ SECURE LOGIN</div>
            <h2 style={{ fontSize: 28, marginBottom: 24 }}>Welcome back</h2>

            <label htmlFor="login-email" style={labelStyle}>Email Address</label>
            <input
              id="login-email"
              className="au-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !busy && sendCode()}
              placeholder="user@domain.com"
              autoFocus
              style={{ marginBottom: 20 }}
            />
            <button
              className="au-pill au-pill-primary"
              onClick={sendCode}
              disabled={busy}
              style={{ width: "100%" }}
            >
              {busy ? "Sending…" : <>Send Access Code <ArrowRight size={18} /></>}
            </button>
          </>
        ) : (
          <>
            <div className="au-marker" style={{ marginBottom: 10 }}>/ VERIFY IDENTITY</div>
            <h2 style={{ fontSize: 28, marginBottom: 16 }}>Enter your code</h2>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: "var(--au-r-input)",
                background: "var(--au-glass)",
                border: "1px solid var(--au-hair)",
                color: "var(--au-text-2)",
                fontSize: 13,
                marginBottom: 20,
              }}
            >
              <Mail size={16} style={{ color: "var(--au-accent-ink)", flex: "none" }} />
              <span>Code sent to <strong style={{ color: "var(--au-text)" }}>{email}</strong></span>
            </div>

            <label htmlFor="login-code" style={labelStyle}>Access Code (6 digits)</label>
            <input
              id="login-code"
              className="au-input"
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g,"").slice(0,6))}
              onKeyDown={e => e.key === "Enter" && !busy && verify()}
              placeholder="000000"
              autoFocus
              maxLength={6}
              style={{
                marginBottom: 20,
                fontFamily: "var(--au-font-mono)",
                fontSize: 24,
                textAlign: "center",
                letterSpacing: 12,
                fontWeight: 700,
              }}
            />
            <button
              className="au-pill au-pill-primary"
              onClick={verify}
              disabled={busy}
              style={{ width: "100%" }}
            >
              {busy ? "Verifying…" : <><KeyRound size={18} /> Authenticate</>}
            </button>
            <button
              className="au-pill au-pill-ghost"
              onClick={() => { setStep(1); setCode(""); }}
              style={{ width: "100%", marginTop: 10 }}
            >
              Use a different email
            </button>
          </>
        )}

        {/* Footer switch */}
        <div
          style={{
            marginTop: 24,
            paddingTop: 18,
            borderTop: "1px solid var(--au-hair)",
            fontSize: 13,
            color: "var(--au-muted)",
            textAlign: "center",
          }}
        >
          New here?{" "}
          <Link
            to="/register"
            style={{ color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 600 }}
          >
            Create an account
          </Link>
        </div>
      </div>

      {/* Bottom credit */}
      <div
        style={{
          position: "absolute",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 2,
          color: "var(--au-muted)",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Bug size={13} style={{ color: "var(--au-accent-ink)" }} />
        Powered by{" "}
        <a
          href={PARENT_URL}
          style={{ color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 700 }}
        >
          GETKHOJO
        </a>
      </div>
    </div>
  );
}
